/**
 * A label colour that can actually be read on a chosen background.
 *
 * The form's buttons take whatever the business set as "Buttons and accents"
 * and used to print white on it unconditionally. Pick a pale accent — white
 * being the obvious one to try — and the label disappeared, so the button
 * looked empty and the setting looked broken.
 *
 * Relative luminance per WCAG 2.x, then the higher-contrast of black or white.
 * The 0.5 threshold is the usual crossover for this pair and lands where the
 * eye expects it: mid-blue keeps white text, mid-yellow flips to black.
 *
 * Kept here rather than in `@flexbyt/shared` because this site consumes the
 * *published* package, so a shared helper would not reach it without a release.
 */
export function readableTextOn(background: string): '#ffffff' | '#111111' {
  const hex = background.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;

  // Anything we can't read is left to the default rather than guessed at.
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return '#ffffff';

  const channel = (offset: number) => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  const luminance =
    0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

  return luminance > 0.5 ? '#111111' : '#ffffff';
}

/**
 * The page behind the form, taken from "Buttons and accents".
 *
 * There used to be a separate background colour, and nothing stopped the two
 * from fighting — a green form on a mauve page. One colour, and the page is a
 * whisper of it over a soft neutral, so every form looks deliberate whatever
 * colour it is given.
 */
export function backdropFor(accent: string): string {
  const hex = accent.trim().replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return '#f6f6f7';

  // 10% of the accent over #f6f6f7.
  const base = [0xf6, 0xf6, 0xf7];
  return (
    '#' +
    base
      .map((value, index) => {
        const mixed = Math.round(value * 0.9 + parseInt(full.slice(index * 2, index * 2 + 2), 16) * 0.1);
        return mixed.toString(16).padStart(2, '0');
      })
      .join('')
  );
}
